import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Percent,
  Crown,
  Calculator,
  ArrowRight,
  Info,
  Zap,
  CheckCircle
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";

/**
 * EarningsFeeBreakdown - Shows stylists their earnings breakdown with platform fees
 */
const EarningsFeeBreakdown = ({ token, membershipTier = "free", onUpgrade }) => {
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);
  const [feeConfig, setFeeConfig] = useState(null);
  const [previewAmount, setPreviewAmount] = useState(100);
  const [previewBreakdown, setPreviewBreakdown] = useState(null);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [earningsRes, configRes] = await Promise.all([
        axios.get(`${API}/stylist/earnings-summary?period=month`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/platform-fees/config`)
      ]);
      
      setEarnings(earningsRes.data);
      setFeeConfig(configRes.data);
      
      // Get preview for default amount
      await fetchPreview(100);
    } catch (err) {
      console.error("Error fetching earnings data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async (amount) => {
    try {
      const res = await axios.get(`${API}/platform-fees/preview?amount=${amount}&payment_method=stripe`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewBreakdown(res.data.breakdown);
    } catch (err) {
      console.error("Error fetching preview:", err);
    }
  };

  const handlePreviewChange = (e) => {
    const amount = parseFloat(e.target.value) || 0;
    setPreviewAmount(amount);
    if (amount > 0) {
      fetchPreview(amount);
    }
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-6">
          <div className="animate-pulse flex flex-col gap-4">
            <div className="h-8 bg-zinc-800 rounded w-1/3"></div>
            <div className="h-24 bg-zinc-800 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const tierInfo = feeConfig?.fee_tiers?.[membershipTier] || { platform_fee_percent: 10 };
  const isPro = membershipTier === "pro" || membershipTier === "elite";

  return (
    <div className="space-y-6">
      {/* Fee Structure Overview */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Percent className="w-5 h-5 text-amber-400" />
            Platform Fee Structure
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Understand how fees work for your Stripe payments
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Current Tier */}
          <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg mb-4">
            <div className="flex items-center gap-3">
              {isPro ? (
                <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <Crown className="w-5 h-5 text-amber-400" />
                </div>
              ) : (
                <div className="w-10 h-10 bg-zinc-700 rounded-full flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-zinc-400" />
                </div>
              )}
              <div>
                <p className="text-white font-medium">Your Current Plan: {membershipTier.toUpperCase()}</p>
                <p className="text-sm text-zinc-400">
                  {tierInfo.platform_fee_percent}% platform fee on Stripe payments
                </p>
              </div>
            </div>
            {!isPro && (
              <Button 
                onClick={onUpgrade}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black"
              >
                <Zap className="w-4 h-4 mr-2" />
                Upgrade to 5%
              </Button>
            )}
          </div>

          {/* Fee Comparison Table */}
          <div className="grid grid-cols-3 gap-3 text-center mb-4">
            <div className={`p-4 rounded-lg border ${membershipTier === 'free' ? 'border-amber-500 bg-amber-500/10' : 'border-zinc-700'}`}>
              <p className="text-2xl font-bold text-white">10%</p>
              <p className="text-sm text-zinc-400">Free Tier</p>
              {membershipTier === 'free' && (
                <Badge className="mt-2 bg-amber-500/20 text-amber-400">Current</Badge>
              )}
            </div>
            <div className={`p-4 rounded-lg border ${membershipTier === 'pro' ? 'border-amber-500 bg-amber-500/10' : 'border-zinc-700'}`}>
              <p className="text-2xl font-bold text-white">5%</p>
              <p className="text-sm text-zinc-400">Pro Tier</p>
              {membershipTier === 'pro' && (
                <Badge className="mt-2 bg-amber-500/20 text-amber-400">Current</Badge>
              )}
            </div>
            <div className={`p-4 rounded-lg border ${membershipTier === 'elite' ? 'border-purple-500 bg-purple-500/10' : 'border-zinc-700'}`}>
              <p className="text-2xl font-bold text-white">5%</p>
              <p className="text-sm text-zinc-400">Elite Tier</p>
              {membershipTier === 'elite' && (
                <Badge className="mt-2 bg-purple-500/20 text-purple-400">Current</Badge>
              )}
            </div>
          </div>

          {/* No Fee Payment Methods */}
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <p className="text-green-400 font-medium">No Platform Fees On:</p>
            </div>
            <div className="flex gap-2">
              <Badge className="bg-zinc-800 text-zinc-300">Zelle</Badge>
              <Badge className="bg-zinc-800 text-zinc-300">CashApp</Badge>
              <Badge className="bg-zinc-800 text-zinc-300">Cash</Badge>
            </div>
            <p className="text-xs text-green-400/70 mt-2">
              Platform fees only apply to Stripe/card payments
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Payment Calculator */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Calculator className="w-5 h-5 text-amber-400" />
            Payment Calculator
          </CardTitle>
          <CardDescription className="text-zinc-400">
            See exactly what you&apos;ll earn from a Stripe payment
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Service Amount ($)</Label>
              <Input
                type="number"
                value={previewAmount}
                onChange={handlePreviewChange}
                className="bg-zinc-800 border-zinc-700 text-2xl font-bold text-white"
                min="0"
                step="5"
              />
            </div>

            {previewBreakdown && (
              <div className="space-y-3 pt-4 border-t border-zinc-800">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400">Client Pays:</span>
                  <span className="text-white font-bold">${previewBreakdown.gross_amount.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center text-red-400">
                  <span className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4" />
                    Stripe Fee ({feeConfig?.stripe_fees?.percent}% + ${feeConfig?.stripe_fees?.fixed}):
                  </span>
                  <span>-${previewBreakdown.stripe_fee.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center text-red-400">
                  <span className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4" />
                    Platform Fee ({previewBreakdown.platform_fee_percent}%):
                  </span>
                  <span>-${previewBreakdown.platform_fee.toFixed(2)}</span>
                </div>
                
                <div className="h-px bg-zinc-700"></div>
                
                <div className="flex justify-between items-center">
                  <span className="text-green-400 font-bold flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    You Receive:
                  </span>
                  <span className="text-2xl font-bold text-green-400">
                    ${previewBreakdown.stylist_net.toFixed(2)}
                  </span>
                </div>

                {/* Upgrade savings */}
                {membershipTier === "free" && previewAmount >= 50 && (
                  <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-amber-400 text-sm">
                      💡 With <strong>Pro</strong>, you&apos;d earn <strong>${(previewBreakdown.gross_amount - previewBreakdown.stripe_fee - (previewBreakdown.gross_amount * 0.05)).toFixed(2)}</strong> 
                      {' '}(save ${(previewBreakdown.platform_fee - (previewBreakdown.gross_amount * 0.05)).toFixed(2)} per transaction)
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Monthly Earnings Summary */}
      {earnings && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              This Month&apos;s Stripe Earnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-white">${earnings.gross_revenue.toFixed(2)}</p>
                <p className="text-xs text-zinc-400">Gross Revenue</p>
              </div>
              <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-400">-${earnings.stripe_fees.toFixed(2)}</p>
                <p className="text-xs text-zinc-400">Stripe Fees</p>
              </div>
              <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-400">-${earnings.platform_fees.toFixed(2)}</p>
                <p className="text-xs text-zinc-400">Platform Fees</p>
              </div>
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-400">${earnings.net_earnings.toFixed(2)}</p>
                <p className="text-xs text-green-400">Net Earnings</p>
              </div>
            </div>

            {earnings.upgrade_savings?.message && (
              <div className="mt-4 p-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-lg flex items-center justify-between">
                <div>
                  <p className="text-amber-400 font-medium">{earnings.upgrade_savings.message}</p>
                  <p className="text-sm text-zinc-400">
                    {earnings.transaction_count} Stripe transactions this month
                  </p>
                </div>
                <Button 
                  onClick={onUpgrade}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                >
                  Upgrade <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Note */}
      <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-300">
          <p className="font-medium mb-1">How Platform Fees Work</p>
          <ul className="list-disc list-inside space-y-1 text-blue-300/80">
            <li>Fees are automatically deducted from Stripe payments</li>
            <li>You receive your earnings directly to your connected bank account</li>
            <li>Zelle, CashApp, and cash payments have NO platform fees</li>
            <li>Upgrade to Pro to cut your platform fee in half (10% → 5%)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EarningsFeeBreakdown;
